/**
 * KpiWidgets.jsx · v101.263.0 · Piezas gráficas (SVG propio, neumorfismo de la plataforma) para Métricas › KPI.
 * Un único renderizador <Widget w={...} /> dibuja cada widget que devuelve /api/v1/kpi/panel/{clave}.
 */
import { useMemo, useState } from "react";

export const C = { bg: "#E4EAF0", surf: "#ECF0F5", ink: "#0A3747", text: "#1E293B", sub: "#475569", mute: "#64748B",
  lime: "#E0E621", info: "#0284C7", warn: "#D97706", bad: "#DC2626", ok: "#16A34A", teal: "#5D8B96" };
export const OUT = "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)";
export const OUT_LG = "-10px -10px 22px rgba(255,255,255,.95), 10px 10px 22px rgba(163,177,198,.55)";
export const OUT_SM = "-3px -3px 8px rgba(255,255,255,.95), 3px 3px 8px rgba(163,177,198,.55)";
export const IN_SM = "inset -2px -2px 5px rgba(255,255,255,.95), inset 2px 2px 5px rgba(163,177,198,.55)";
export const MONO = "'JetBrains Mono', ui-monospace, monospace";
export const card = { padding: 22, borderRadius: 26, background: C.surf, boxShadow: OUT_LG, display: "flex",
  flexDirection: "column", gap: 14, minWidth: 0 };
export const well = { borderRadius: 16, background: C.bg, boxShadow: IN_SM };
export const PAL = ["#0A3747", "#0284C7", "#D97706", "#5D8B96", "#16A34A", "#DC2626", "#7C3AED", "#A9C08A", "#E1582A", "#64748B"];
const HEAT = ["#E4EAF0", "#C9D9DF", "#9DBAC3", "#5D8B96", "#2F5F6E", "#0A3747"];

export const fmt = (v, u = "") => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const n = Number(v);
  const s = Math.abs(n) >= 1000 ? n.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : Number.isInteger(n) ? String(n) : n.toFixed(1);
  if (u === "USD") return `$${s}`;
  return u === "%" ? `${s}%` : u ? `${s} ${u}` : s;
};

function Titulo({ w }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{w.titulo}</span>
      {w.nota && <span style={{ fontSize: 12, color: C.sub }}>{w.nota}</span>}
    </div>
  );
}

const Vacio = () => <div style={{ ...well, padding: 18, fontSize: 12, color: C.mute, textAlign: "center" }}>Sin datos en el período</div>;

/* ── Sparkline ── */
export function Spark({ valores = [], color = C.ink, h = 42 }) {
  const v = valores.map((x) => Number(x) || 0);
  if (v.length < 2) return <div style={{ height: h }} />;
  const max = Math.max(...v, 1), W = 160;
  const pts = v.map((y, i) => [(i / (v.length - 1)) * W, h - 4 - (y / max) * (h - 8)]);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const id = `sp${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg viewBox={`0 0 ${W} ${h}`} width="100%" height={h} preserveAspectRatio="none" aria-hidden="true">
      <defs><linearGradient id={id} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".28" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <path d={`${d} L${W},${h} L0,${h} Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ── KPIs ── */
function Kpis({ w }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
      {(w.items || []).map((k) => (
        <div key={k.label} style={{ ...well, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.sub }}>{k.label}</span>
          <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-.02em",
            color: k.tono === "malo" ? C.bad : k.tono === "bueno" ? C.ok : C.ink }}>{fmt(k.valor, k.unidad)}</span>
          {k.sub && <span style={{ fontFamily: MONO, fontSize: 11, color: C.mute }}>{k.sub}</span>}
        </div>
      ))}
    </div>
  );
}

/* ── Serie temporal: línea / área / barras apiladas ── */
function Serie({ w }) {
  const [hover, setHover] = useState(null);
  const x = w.x || [], series = w.series || [];
  if (!x.length || !series.length) return <Vacio />;
  const W = 1240, H = 250, L = 46, B = 28, T = 12;
  const apil = w.estilo === "apiladas";
  const tot = x.map((_, i) => series.reduce((a, s) => a + (Number(s.valores[i]) || 0), 0));
  const max = Math.max(1, ...(apil ? tot : series.flatMap((s) => s.valores.map((v) => Number(v) || 0))));
  const sx = (i) => L + (i + 0.5) * ((W - L) / x.length);
  const sy = (v) => T + (H - T - B) * (1 - v / max);
  const step = Math.max(1, Math.ceil(x.length / 16));
  const bw = Math.max(2, ((W - L) / x.length) * 0.62);
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={w.titulo} onMouseLeave={() => setHover(null)}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={L} x2={W} y1={sy(max * f)} y2={sy(max * f)} stroke="#CBD5E1" strokeDasharray="3 5" />
            <text x={L - 6} y={sy(max * f) + 4} textAnchor="end" fontSize="12" fill={C.mute} fontFamily="JetBrains Mono">{fmt(max * f)}</text>
          </g>
        ))}
        {x.map((lab, i) => (i % step === 0 ? <text key={lab + i} x={sx(i)} y={H - 6} textAnchor="middle" fontSize="12" fill={C.mute} fontFamily="JetBrains Mono">{lab}</text> : null))}
        {apil ? x.map((_, i) => {
          let acc = 0;
          return series.map((s, k) => {
            const v = Number(s.valores[i]) || 0;
            const y0 = sy(acc), y1 = sy(acc + v);
            acc += v;
            return v ? <rect key={`${i}-${k}`} x={sx(i) - bw / 2} y={y1} width={bw} height={Math.max(0, y0 - y1)} rx="3" fill={PAL[k % PAL.length]} opacity={hover === null || hover === i ? 1 : 0.45} /> : null;
          });
        }) : series.map((s, k) => {
          const pts = s.valores.map((v, i) => [sx(i), sy(Number(v) || 0)]);
          const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
          const col = PAL[k % PAL.length];
          return (
            <g key={s.nombre}>
              {w.estilo === "area" && <path d={`${d} L${pts[pts.length - 1][0]},${sy(0)} L${pts[0][0]},${sy(0)} Z`} fill={col} opacity=".12" />}
              <path d={d} fill="none" stroke={col} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
              {hover !== null && <circle cx={pts[hover][0]} cy={pts[hover][1]} r="4.5" fill={C.surf} stroke={col} strokeWidth="2.4" />}
            </g>
          );
        })}
        {x.map((_, i) => <rect key={`h${i}`} x={sx(i) - (W - L) / x.length / 2} y={T} width={(W - L) / x.length} height={H - T - B} fill="transparent" onMouseEnter={() => setHover(i)} />)}
        {hover !== null && <line x1={sx(hover)} x2={sx(hover)} y1={T} y2={H - B} stroke={C.ink} strokeOpacity=".25" />}
      </svg>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6 }}>
        {series.map((s, k) => (
          <span key={s.nombre} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.sub }}>
            <i style={{ width: 10, height: 10, borderRadius: 3, background: PAL[k % PAL.length] }} />{s.nombre}
            {hover !== null && <b style={{ fontFamily: MONO, color: C.ink }}>{fmt(s.valores[hover])}</b>}
          </span>
        ))}
        {hover !== null && <span style={{ fontFamily: MONO, fontSize: 12, color: C.mute, marginLeft: "auto" }}>{x[hover]}{apil ? ` · total ${fmt(tot[hover])}` : ""}</span>}
      </div>
    </div>
  );
}

/* ── Ranking de barras horizontales ── */
function Barras({ w }) {
  const items = (w.items || []).filter((i) => i.valor !== null && i.valor !== undefined);
  if (!items.length) return <Vacio />;
  const max = Math.max(...items.map((i) => Number(i.valor) || 0), w.meta || 0, 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((i, k) => {
        const pct = Math.max(2, (100 * (Number(i.valor) || 0)) / max);
        const sobre = w.meta && i.valor > w.meta;
        return (
          <div key={i.label + k} style={{ display: "grid", gridTemplateColumns: "minmax(110px, 34%) 1fr auto", gap: 12, alignItems: "center" }}>
            <span title={i.label} style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.label}</span>
            <div style={{ ...well, height: 14, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 0, width: `${pct}%`, borderRadius: 16,
                background: sobre ? `linear-gradient(90deg, ${C.ink}, ${C.warn} 70%, ${C.bad})` : `linear-gradient(90deg, ${C.ink}, ${k === 0 ? C.info : C.teal})` }} />
              {w.meta ? <div title={`Meta ${w.meta}`} style={{ position: "absolute", top: -2, bottom: -2, left: `${(100 * w.meta) / max}%`, width: 2, background: C.bad, opacity: 0.6 }} /> : null}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.ink, textAlign: "right", minWidth: 64 }}>
              {fmt(i.valor, w.unidad)}{i.extra && <span style={{ display: "block", fontSize: 10, fontWeight: 500, color: C.mute }}>{i.extra}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Dona segmentada ── */
function Donut({ w }) {
  const items = (w.items || []).filter((i) => Number(i.valor) > 0);
  const [act, setAct] = useState(null);
  if (!items.length) return <Vacio />;
  const tot = items.reduce((a, i) => a + Number(i.valor), 0);
  const R = 78, r0 = 52, cx = 100, cy = 100;
  const solo = items.length === 1;
  let a0 = -Math.PI / 2;
  const arcs = items.map((i, k) => {
    const ang = (2 * Math.PI * Number(i.valor)) / tot, gap = items.length > 1 ? 0.03 : 0;
    const a1 = a0 + ang;
    const p = (a, rad) => [cx + rad * Math.cos(a), cy + rad * Math.sin(a)];
    const [s0, s1, e0, e1] = [p(a0 + gap, R), p(a1 - gap, R), p(a1 - gap, r0), p(a0 + gap, r0)];
    const big = ang - 2 * gap > Math.PI ? 1 : 0;
    const d = `M${s0} A${R},${R} 0 ${big} 1 ${s1} L${e0} A${r0},${r0} 0 ${big} 0 ${e1} Z`;
    a0 = a1;
    return { d, col: PAL[k % PAL.length], i, k };
  });
  const sel = act !== null ? items[act] : null;
  return (
    <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ width: 200, height: 200, borderRadius: "50%", background: C.surf, boxShadow: OUT, flexShrink: 0 }}>
        <svg viewBox="0 0 200 200" width="200" height="200" role="img" aria-label={w.titulo}>
          {solo && <circle cx="100" cy="100" r="65" fill="none" stroke={PAL[0]} strokeWidth="26" />}
          {!solo && arcs.map((a) => <path key={a.k} d={a.d} fill={a.col} opacity={act === null || act === a.k ? 1 : 0.35}
            onMouseEnter={() => setAct(a.k)} onMouseLeave={() => setAct(null)} style={{ transition: "opacity .2s" }} />)}
          <text x="100" y="96" textAnchor="middle" fontSize="24" fontWeight="800" fill={C.ink}>{sel ? `${((100 * sel.valor) / tot).toFixed(0)}%` : fmt(tot)}</text>
          <text x="100" y="116" textAnchor="middle" fontSize="10" fill={C.mute}>{sel ? fmt(sel.valor) : "total"}</text>
        </svg>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 180 }}>
        {arcs.map((a) => (
          <div key={a.k} onMouseEnter={() => setAct(a.k)} onMouseLeave={() => setAct(null)}
            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "default" }}>
            <i style={{ width: 10, height: 10, borderRadius: 3, background: a.col, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{a.i.label}</span>
            <b style={{ fontFamily: MONO, color: C.ink }}>{fmt(a.i.valor)}</b>
            <span style={{ fontFamily: MONO, fontSize: 11, color: C.mute, width: 44, textAlign: "right" }}>{((100 * a.i.valor) / tot).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Anillos concéntricos (0–100 %) ── */
function Radial({ w }) {
  const items = (w.items || []).filter((i) => i.valor !== null && i.valor !== undefined);
  if (!items.length) return <Vacio />;
  const cols = [C.ok, C.info, C.teal, C.ink, C.warn, C.bad];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
      <div style={{ width: 210, height: 210, borderRadius: "50%", background: C.surf, boxShadow: OUT, flexShrink: 0 }}>
        <svg viewBox="0 0 210 210" width="210" height="210" style={{ transform: "rotate(-90deg)" }} role="img" aria-label={w.titulo}>
          {items.map((i, k) => {
            const r = 88 - k * 17, c = 2 * Math.PI * r;
            return (
              <g key={i.label}>
                <circle cx="105" cy="105" r={r} fill="none" stroke="#D5DEE7" strokeWidth="11" />
                <circle cx="105" cy="105" r={r} fill="none" stroke={cols[k % cols.length]} strokeWidth="11" strokeLinecap="round"
                  strokeDasharray={`${(c * Math.min(100, i.valor)) / 100} ${c}`} />
              </g>
            );
          })}
        </svg>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 160 }}>
        {items.map((i, k) => (
          <div key={i.label} style={{ ...well, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
            <i style={{ width: 10, height: 10, borderRadius: "50%", background: cols[k % cols.length] }} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: C.text }}>{i.label}</span>
            <b style={{ fontFamily: MONO, fontSize: 14, color: C.ink }}>{fmt(i.valor, "%")}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Mapa de calor ── */
function Heat({ w }) {
  const filas = w.filas || [], cols = w.cols || [], vals = w.valores || [];
  const max = Math.max(1, ...vals.flat().map((v) => Number(v) || 0));
  if (!filas.length || !cols.length) return <Vacio />;
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: `minmax(110px, auto) repeat(${cols.length}, minmax(${cols.length > 12 ? 26 : 56}px, 1fr))`, gap: 4, minWidth: cols.length > 12 ? 760 : 0 }}>
        <span />
        {cols.map((c) => <span key={c} title={c} style={{ fontFamily: MONO, fontSize: 10, color: C.mute, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c}</span>)}
        {filas.map((f, i) => (
          <FragmentRow key={f} f={f} row={vals[i] || []} cols={cols} max={max} unidad={w.unidad} />
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 11, color: C.mute }}>
        menos {HEAT.map((h) => <i key={h} style={{ width: 18, height: 8, borderRadius: 3, background: h }} />)} más
      </div>
    </div>
  );
}
function FragmentRow({ f, row, cols, max, unidad }) {
  return (
    <>
      <span title={f} style={{ fontSize: 11, fontWeight: 600, color: C.text, alignSelf: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f}</span>
      {cols.map((c, j) => {
        const v = Number(row[j]) || 0;
        const idx = v ? Math.min(HEAT.length - 1, 1 + Math.floor(((HEAT.length - 2) * v) / max)) : 0;
        return (
          <div key={c} title={`${f} · ${c}: ${fmt(v, unidad)}`} style={{ height: 28, borderRadius: 8, background: HEAT[idx],
            boxShadow: v ? OUT_SM : IN_SM, display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: MONO, fontSize: 10, fontWeight: 700, color: idx >= 3 ? "#fff" : C.sub }}>
            {v ? (cols.length > 12 ? "" : fmt(v)) : ""}
          </div>
        );
      })}
    </>
  );
}

/* ── Constelación de burbujas ── */
function Burbujas({ w }) {
  const items = (w.items || []).filter((i) => i.x !== null && i.y !== null && i.x !== undefined && i.y !== undefined);
  const [act, setAct] = useState(null);
  if (!items.length) return <Vacio />;
  const W = 760, H = 300, L = 46, B = 30, T = 14, R = 16;
  const mx = Math.max(...items.map((i) => Number(i.x)), 1), my = Math.max(...items.map((i) => Number(i.y)), 1);
  const mr = Math.max(...items.map((i) => Number(i.r) || 0), 1);
  const ax = items.reduce((a, i) => a + Number(i.x), 0) / items.length, ay = items.reduce((a, i) => a + Number(i.y), 0) / items.length;
  const sx = (v) => L + ((W - L - R) * v) / mx, sy = (v) => T + (H - T - B) * (1 - v / my);
  const ini = (s) => String(s).split(/[\s·/-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={w.titulo}>
        <rect x={L} y={T} width={W - L - R} height={H - T - B} rx="18" fill={C.bg} />
        <line x1={sx(ax)} x2={sx(ax)} y1={T} y2={H - B} stroke={C.mute} strokeDasharray="4 6" />
        <line x1={L} x2={W - R} y1={sy(ay)} y2={sy(ay)} stroke={C.mute} strokeDasharray="4 6" />
        <text x={W - R - 6} y={H - B - 8} textAnchor="end" fontSize="11" fill={C.mute}>{w.ejeX} →</text>
        <text x={L + 8} y={T + 16} fontSize="11" fill={C.mute}>↑ {w.ejeY}</text>
        {items.map((i, k) => {
          const rad = 9 + 22 * Math.sqrt((Number(i.r) || 0) / mr);
          return (
            <g key={i.label + k} onMouseEnter={() => setAct(k)} onMouseLeave={() => setAct(null)} style={{ cursor: "default" }}>
              <circle cx={sx(i.x)} cy={sy(i.y)} r={rad} fill={PAL[k % PAL.length]} opacity={act === null || act === k ? 0.88 : 0.25}
                stroke="#fff" strokeWidth="2" />
              {rad > 13 && <text x={sx(i.x)} y={sy(i.y) + 4} textAnchor="middle" fontSize="10" fontWeight="800" fill="#fff">{ini(i.label)}</text>}
            </g>
          );
        })}
      </svg>
      <div style={{ ...well, padding: "8px 12px", fontSize: 12, color: C.sub, minHeight: 34 }}>
        {act !== null ? <><b style={{ color: C.ink }}>{items[act].label}</b> · {w.ejeX}: <b>{fmt(items[act].x)}</b> · {w.ejeY}: <b>{fmt(items[act].y)}</b>{items[act].r ? <> · tamaño: <b>{fmt(items[act].r)}</b></> : null}</>
          : "Pase el mouse sobre una burbuja. Las líneas punteadas son los promedios."}
      </div>
    </div>
  );
}

/* ── Duelo A vs B (barras espejadas) ── */
function Duelo({ w }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 150px 1fr", fontSize: 13, fontWeight: 800 }}>
        <span style={{ textAlign: "right", color: C.ink }}>{w.a}</span><span /><span style={{ color: C.info }}>{w.b}</span>
      </div>
      {(w.metricas || []).map((m) => {
        const a = Number(m.a) || 0, b = Number(m.b) || 0, max = Math.max(a, b, 1);
        const gana = m.mejor && m.a !== null && m.b !== null && a !== b ? ((m.mejor === "alto") === (a > b) ? "a" : "b") : null;
        return (
          <div key={m.label} style={{ display: "grid", gridTemplateColumns: "1fr 150px 1fr", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
              <b style={{ fontFamily: MONO, fontSize: 13, whiteSpace: "nowrap", color: gana === "a" ? C.ok : C.ink }}>{fmt(m.a, m.unidad)}</b>
              <div style={{ ...well, height: 14, width: "70%", display: "flex", justifyContent: "flex-end", overflow: "hidden" }}>
                <div style={{ width: `${(100 * a) / max}%`, background: C.ink, borderRadius: 16 }} />
              </div>
            </div>
            <span style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: C.sub }}>{m.label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ ...well, height: 14, width: "70%", overflow: "hidden" }}>
                <div style={{ width: `${(100 * b) / max}%`, height: "100%", background: C.info, borderRadius: 16 }} />
              </div>
              <b style={{ fontFamily: MONO, fontSize: 13, whiteSpace: "nowrap", color: gana === "b" ? C.ok : C.info }}>{fmt(m.b, m.unidad)}</b>
            </div>
          </div>
        );
      })}
      <span style={{ fontSize: 11, color: C.mute }}>En verde, el mejor lado cuando la métrica tiene un sentido claro.</span>
    </div>
  );
}

/* ── Barras 100 % apiladas ── */
function Apiladas({ w }) {
  const items = w.items || [], series = w.series || [];
  if (!items.length) return <Vacio />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {series.map((s, k) => <span key={s} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.sub }}>
          <i style={{ width: 10, height: 10, borderRadius: 3, background: PAL[k % PAL.length] }} />{(w.nombres || {})[s] || s}</span>)}
      </div>
      {items.map((i) => {
        const tot = series.reduce((a, s) => a + (Number(i[s]) || 0), 0) || 1;
        return (
          <div key={i.label} style={{ display: "grid", gridTemplateColumns: "minmax(110px, 30%) 1fr 54px", gap: 12, alignItems: "center" }}>
            <span title={i.label} style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.label}</span>
            <div style={{ ...well, height: 18, display: "flex", overflow: "hidden", padding: 2, gap: 2 }}>
              {series.map((s, k) => {
                const v = Number(i[s]) || 0;
                return v ? <div key={s} title={`${(w.nombres || {})[s] || s}: ${v} (${((100 * v) / tot).toFixed(0)}%)`}
                  style={{ width: `${(100 * v) / tot}%`, background: PAL[k % PAL.length], borderRadius: 12, color: "#fff", fontSize: 9, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>{(100 * v) / tot >= 14 ? `${((100 * v) / tot).toFixed(0)}%` : ""}</div> : null;
              })}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 11, color: C.mute, textAlign: "right" }}>{fmt(tot)}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Tabla ── */
function Tabla({ w }) {
  const [orden, setOrden] = useState(null);
  const rows = useMemo(() => {
    const r = [...(w.rows || [])];
    if (orden) r.sort((a, b) => (orden.desc ? -1 : 1) * (((a[orden.k] ?? -1e9) > (b[orden.k] ?? -1e9)) ? 1 : -1));
    return r;
  }, [w.rows, orden]);
  if (!rows.length) return <Vacio />;
  return (
    <div style={{ ...well, padding: 6, overflow: "auto", maxHeight: 420 }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 4px", fontSize: 12 }}>
        <thead><tr>{w.cols.map((c) => (
          <th key={c.k} onClick={() => setOrden((o) => ({ k: c.k, desc: o?.k === c.k ? !o.desc : true }))}
            style={{ textAlign: "left", padding: "6px 10px", color: C.sub, fontWeight: 700, cursor: "pointer", position: "sticky", top: 0, background: C.bg }}>
            {c.label}{orden?.k === c.k ? (orden.desc ? " ↓" : " ↑") : ""}</th>))}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} style={{ background: C.surf }}>
            {w.cols.map((c, j) => <td key={c.k} style={{ padding: "8px 10px", color: C.text, fontFamily: j ? MONO : "Inter",
              borderRadius: j === 0 ? "10px 0 0 10px" : j === w.cols.length - 1 ? "0 10px 10px 0" : 0 }}>{typeof r[c.k] === "number" ? fmt(r[c.k]) : (r[c.k] ?? "—")}</td>)}
          </tr>))}</tbody>
      </table>
    </div>
  );
}

const MAP = { kpis: Kpis, serie: Serie, barras: Barras, donut: Donut, radial: Radial, heat: Heat, burbujas: Burbujas,
  duelo: Duelo, apiladas: Apiladas, tabla: Tabla };

export function Widget({ w }) {
  const Cmp = MAP[w.tipo];
  if (!Cmp) return null;
  const ancho = ["serie", "heat", "burbujas", "tabla", "kpis"].includes(w.tipo);
  return (
    <section style={{ ...card, gridColumn: ancho ? "1 / -1" : "auto" }} data-testid={`kpi-w-${w.tipo}`}>
      <Titulo w={w} />
      <Cmp w={w} />
    </section>
  );
}
